import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import WebsiteDomainSettings, {
  type WebsiteDomain,
} from "@/components/website_domain_settings";
import SettingsLayout from "@/layouts/settings_layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { sitePath, userWebsitePath, userWebsiteLocationPath } from "@/routes";

type Site = {
  id: string;
  brand_name: string;
  headline_en: string;
  headline_ro: string;
  description_en: string;
  description_ro: string;
  published: boolean;
};
type Location = {
  id: string;
  name: string;
  locale: string;
  currency: string;
  description: string;
  published: boolean;
  public_offering_ids: string[];
  offerings: { id: string; name: string }[];
};

export default function WebsiteSettings({
  site,
  locations,
  domain,
  domain_eligible,
  domain_target,
}: {
  site: Site;
  locations: Location[];
  domain: WebsiteDomain | null;
  domain_eligible: boolean;
  domain_target: string | null;
}) {
  const { t } = useTranslation();
  const { errors } = usePage().props;
  return (
    <SettingsLayout>
      <Head title={t("website.title")} />
      <div className="space-y-6">
        <header className="space-y-3">
          <h1 className="text-xl font-semibold">{t("website.title")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("website.public_help")}
          </p>
          {site.published ? (
            <Button
              variant="outline"
              render={<Link href={sitePath(site.id)} />}
            >
              {t("website.view")}
            </Button>
          ) : (
            <p className="text-muted-foreground text-sm">
              {t("website.unpublished")}
            </p>
          )}
        </header>
        {Object.keys(errors || {}).length > 0 && (
          <div role="alert" className="text-destructive text-sm">
            {Object.values(errors).map((error, index) => (
              <p key={index}>{error}</p>
            ))}
          </div>
        )}
        <nav className="flex flex-wrap gap-3" aria-label={t("website.title")}>
          {["content", "locations", "domain"].map((section) => (
            <a
              key={section}
              className="inline-flex min-h-11 items-center rounded-md border px-4 text-sm underline"
              href={`#website-${section}`}
            >
              {t(`access.website_${section}`)}
            </a>
          ))}
        </nav>
        <Card id="website-content">
          <CardHeader>
            <CardTitle>{t("access.website_content")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Form action={userWebsitePath()} method="patch">
              {({ processing }) => (
                <div className="space-y-5">
                  {(
                    [
                      "brand_name",
                      "headline_en",
                      "description_en",
                      "headline_ro",
                      "description_ro",
                    ] as const
                  ).map((field) => (
                    <div key={field} className="space-y-2">
                      <Label htmlFor={`website-${field}`}>
                        {t(`website.${field}`)}
                      </Label>
                      {field.startsWith("description") ? (
                        <Textarea
                          id={`website-${field}`}
                          name={`company_website[${field}]`}
                          defaultValue={site[field]}
                          maxLength={10000}
                          aria-invalid={!!errors?.[field]}
                        />
                      ) : (
                        <Input
                          id={`website-${field}`}
                          name={`company_website[${field}]`}
                          defaultValue={site[field]}
                          required={field === "brand_name"}
                          maxLength={field === "brand_name" ? 200 : 300}
                          aria-invalid={!!errors?.[field]}
                        />
                      )}
                    </div>
                  ))}
                  <div className="flex items-center gap-3">
                    <input
                      type="hidden"
                      name="company_website[published]"
                      value="0"
                    />
                    <Checkbox
                      id="publish-site"
                      name="company_website[published]"
                      value="1"
                      defaultChecked={site.published}
                    />
                    <Label htmlFor="publish-site">
                      {t("website.publish_site")}
                    </Label>
                  </div>
                  <Button type="submit" disabled={processing}>
                    {t("website.save")}
                  </Button>
                </div>
              )}
            </Form>
          </CardContent>
        </Card>
        <section id="website-locations" className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t("access.website_locations")}
          </h2>
          {locations.map((location) => (
            <Card key={location.id}>
              <CardHeader>
                <CardTitle className="break-words">{location.name}</CardTitle>
                <p className="text-muted-foreground text-sm">
                  {t(`website.${location.locale}`)} · {location.currency}
                </p>
              </CardHeader>
              <CardContent>
                <Form
                  action={userWebsiteLocationPath(location.id)}
                  method="patch"
                >
                  {({ processing }) => (
                    <div className="space-y-5">
                      <div className="flex items-center gap-3">
                        <input
                          type="hidden"
                          name="website_location[published]"
                          value="0"
                        />
                        <Checkbox
                          id={`publish-${location.id}`}
                          name="website_location[published]"
                          value="1"
                          defaultChecked={location.published}
                        />
                        <Label htmlFor={`publish-${location.id}`}>
                          {t("website.publish_location")}
                        </Label>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`description-${location.id}`}>
                          {t("website.location_description")}
                        </Label>
                        <Textarea
                          id={`description-${location.id}`}
                          name="website_location[description]"
                          defaultValue={location.description}
                          maxLength={10000}
                        />
                      </div>
                      <fieldset className="space-y-3">
                        <legend className="mb-2 text-sm font-medium">
                          {t("website.public_services")}
                        </legend>
                        <p className="text-muted-foreground text-sm">
                          {t("website.services_help")}
                        </p>
                        <input
                          type="hidden"
                          name="website_location[public_offering_ids][]"
                          value=""
                        />
                        {location.offerings.map((offering) => (
                          <div
                            className="flex items-center gap-3"
                            key={offering.id}
                          >
                            <Checkbox
                              id={`offering-${offering.id}`}
                              name="website_location[public_offering_ids][]"
                              value={offering.id}
                              defaultChecked={location.public_offering_ids.includes(
                                offering.id,
                              )}
                            />
                            <Label
                              htmlFor={`offering-${offering.id}`}
                              className="min-w-0 break-words"
                            >
                              {offering.name}
                            </Label>
                          </div>
                        ))}
                      </fieldset>
                      <Button type="submit" disabled={processing}>
                        {t("website.save_location")}
                      </Button>
                    </div>
                  )}
                </Form>
              </CardContent>
            </Card>
          ))}
        </section>
        <section id="website-domain" className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t("access.website_domain")}
          </h2>
          <WebsiteDomainSettings
            domain={domain}
            eligible={domain_eligible}
            target={domain_target}
          />
        </section>
      </div>
    </SettingsLayout>
  );
}
